import { getValueFormat } from '@baurine/grafana-value-formats'
import dayjs from 'dayjs'

import {
  ComponentTypes,
  clusterComponentNameMap,
  getGrafanaDashboardLink,
  MetricItemExtend
} from './types'

export const metricItems: MetricItemExtend[] = [
  {
    base: {
      title: 'SLA',
      desc: 'SLA of current month',
      suffix: '%',
      link: '',
      visible: false
    },
    converter: (base, clusterMetrics, _, isInternalUser, org, cluster) => {
      if (!clusterMetrics) return base

      const val = Number(clusterMetrics.sla)
      const grafanaLink = `https://grafana.oauth.prod.aws.tidbcloud.com/d/sla-per-cluster/sla-per-cluster?orgId=1&var-month=${dayjs().format(
        'YYYY/M'
      )}&var-cluster_id=${cluster?.id}&var-cluster_name=${
        cluster?.name
      }&var-tenant_id=${org?.externalID}&var-tenant_name=${org?.name}`
      return {
        ...base,
        link: grafanaLink,
        visible: isInternalUser,
        value: (val * 100).toFixed(2),
        warning: isNaN(val) || val < 0.9999
      }
    }
  },
  {
    base: {
      title: 'Used Storage Size',
      desc: 'Storage of TiKV and TiFlash nodes',
      suffix: 'GiB',
      link: '',
      visible: true
    },
    converter: (base, clusterMetrics, grafanaDashboards) => {
      if (!clusterMetrics) return base

      const val = Number(clusterMetrics.storageSize ?? '0')
      const sizeStr = getValueFormat('bytes')(val, 2)
      const [v, unit] = sizeStr.split(' ')
      const grafanaLink = getGrafanaDashboardLink('tikv', grafanaDashboards)

      return {
        ...base,
        value: v,
        suffix: unit,
        link: grafanaLink
      }
    }
  },
  {
    base: {
      title: 'Min Pod Uptime',
      subtitle: 'among all nodes',
      desc: 'The one with the smallest uptime among all nodes',
      suffix: 'hours (TiDB)',
      link: '',
      visible: true
    },
    converter: (base, clusterMetrics, grafanaDashboards) => {
      if (!clusterMetrics) return base

      const { component, value } = clusterMetrics.minUpTime!
      const val = Number(value ?? '0')
      const timeStr = getValueFormat('s')(val, 2)
      let [v, unit] = timeStr.split(' ')
      if (unit !== 's' && Number(v) > 1) {
        unit += 's'
      }
      const componentName = clusterComponentNameMap[component as ComponentTypes]
      const grafanaLink = getGrafanaDashboardLink(
        component as ComponentTypes,
        grafanaDashboards
      )

      return {
        ...base,
        value: v,
        suffix: `${unit} (${componentName})`,
        warning: val < 8 * 60 * 60,
        link: grafanaLink
      }
    }
  },
  {
    base: {
      title: 'Max CPU',
      subtitle: 'among all nodes',
      desc: 'Calculate the value (after normalization) of the CPU usage of all nodes in the last hour, and show the maximum value',
      suffix: '% (TiDB)',
      link: '',
      visible: true
    },
    converter: (base, clusterMetrics, grafanaDashboards) => {
      if (!clusterMetrics) return base

      const { component, value } = clusterMetrics.maxCpu!
      const val = Number(value ?? '0')
      const componentName = clusterComponentNameMap[component as ComponentTypes]
      const grafanaLink = getGrafanaDashboardLink(
        component as ComponentTypes,
        grafanaDashboards
      )

      return {
        ...base,
        value: (val * 100).toFixed(0),
        warning: val > 0.7,
        suffix: `% (${componentName})`,
        link: grafanaLink
      }
    }
  },
  {
    base: {
      title: 'Max Memory',
      subtitle: 'among all nodes',
      desc: 'Calculate the value (after normalization) of the Memory usage of all nodes in the last hour, and show the maximum value',
      suffix: '% (TiDB)',
      link: '',
      visible: true
    },
    converter: (base, clusterMetrics, grafanaDashboards) => {
      if (!clusterMetrics) return base

      const { component, value } = clusterMetrics.maxMemory!
      const val = Number(value ?? '0')
      const componentName = clusterComponentNameMap[component as ComponentTypes]
      const grafanaLink = getGrafanaDashboardLink(
        component as ComponentTypes,
        grafanaDashboards
      )

      return {
        ...base,
        value: (val * 100).toFixed(0),
        warning: val > 0.85,
        suffix: `% (${componentName})`,
        link: grafanaLink
      }
    }
  }
]
